export type CommunicationEnvelope = {
  type: string;
  payload?: { conversation_id?: string; account_id?: string };
};
export type CommunicationConnection =
  'connecting' | 'connected' | 'reconnecting' | 'offline' | 'unavailable';

// Each mounted messaging surface owns one transport. Conversation changes are
// subscriptions, not new connections; web and admin cookies stay independent.
export function openCommunicationSocket({
  url,
  validateSession,
  onEvent,
  onState,
}: {
  url: string;
  validateSession: (signal: AbortSignal) => Promise<boolean>;
  onEvent: (event: CommunicationEnvelope) => void | boolean;
  onState: (state: CommunicationConnection) => void;
}) {
  let stopped = false;
  let suspended = false;
  let socket: WebSocket | null = null;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let validation: AbortController | undefined;
  let generation = 0;
  let failures = 0;
  let offline = navigator.onLine === false;

  const invalidate = () => {
    ++generation;
    if (timer !== undefined) clearTimeout(timer);
    timer = undefined;
    validation?.abort();
    validation = undefined;
    const previous = socket;
    socket = null;
    if (previous) {
      previous.onmessage = previous.onclose = previous.onerror = null;
      previous.close();
    }
  };
  const stop = () => {
    stopped = true;
    invalidate();
    window.removeEventListener('online', online);
    window.removeEventListener('offline', disconnectedNetwork);
    window.removeEventListener('pagehide', hide);
    window.removeEventListener('pageshow', show);
  };
  const retry = () => {
    if (stopped || suspended || offline || timer !== undefined) return;
    onState('reconnecting');
    timer = setTimeout(
      () => {
        timer = undefined;
        connect();
      },
      Math.min(30000, 1000 * 2 ** Math.min(failures++, 5)),
    );
  };
  const disconnected = async (currentGeneration: number) => {
    if (stopped || generation !== currentGeneration) return;
    invalidate();
    if (navigator.onLine === false) offline = true;
    if (offline) {
      onState('offline');
      return;
    }
    onState('reconnecting');
    const controller = new AbortController();
    validation = controller;
    try {
      const valid = await validateSession(controller.signal);
      if (stopped || controller.signal.aborted) return;
      if (!valid) {
        onState('unavailable');
        stop();
        return;
      }
    } catch {
      if (stopped || controller.signal.aborted) return;
      // Temporary HTTP/network errors should not revoke the user's session.
    } finally {
      if (validation === controller) validation = undefined;
    }
    retry();
  };
  const connect = () => {
    if (stopped || suspended || offline || socket || validation) return;
    const currentGeneration = ++generation;
    try {
      const current = new WebSocket(url);
      socket = current;
      current.onmessage = (event) => {
        if (stopped || generation !== currentGeneration || socket !== current) return;
        let value: CommunicationEnvelope;
        try {
          value = JSON.parse(event.data);
          if (!value || typeof value.type !== 'string') return;
        } catch {
          return;
        }
        if (value.type === 'connected') {
          failures = 0;
          onState('connected');
        }
        if (onEvent(value) === false) {
          onState('unavailable');
          stop();
        }
      };
      current.onclose = current.onerror = () => void disconnected(currentGeneration);
    } catch {
      void disconnected(currentGeneration);
    }
  };
  const online = () => {
    if (stopped) return;
    offline = false;
    if (timer !== undefined) clearTimeout(timer);
    timer = undefined;
    // An online event is not a reason to replace a healthy connection.
    connect();
  };
  const disconnectedNetwork = () => {
    if (stopped) return;
    offline = true;
    invalidate();
    onState('offline');
  };
  const hide = () => {
    if (stopped) return;
    suspended = true;
    invalidate();
  };
  const show = () => {
    if (stopped || !suspended) return;
    suspended = false;
    offline = navigator.onLine === false;
    onState(offline ? 'offline' : 'reconnecting');
    connect();
  };
  window.addEventListener('online', online);
  window.addEventListener('offline', disconnectedNetwork);
  window.addEventListener('pagehide', hide);
  window.addEventListener('pageshow', show);
  onState(offline ? 'offline' : 'connecting');
  connect();
  return stop;
}
